import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Loader2, Check, AlertTriangle, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared support contact form. Posts to /api/support/contact.
 *
 * Props:
 *  - layout: "panel" (default, fixed-width modal) | "page" (full-width inline)
 *  - prefill: { subject, message, last_chat_question, chat_context_summary, escalated_to_support, source_page }
 *  - sourcePage: shorthand for source_page when no prefill provided
 *  - showHeader: whether to render the inline H2 (page layout)
 *  - onSubmitted: () => void called after a successful submit
 *  - submitLabel: optional override
 */
export default function SupportContactForm({
  layout = 'panel',
  prefill = {},
  sourcePage,
  showHeader = true,
  onSubmitted,
  submitLabel,
}) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const ja = lang === 'ja';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(prefill.subject || '');
  const [message, setMessage] = useState(prefill.message || '');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (user && user.email) setEmail(user.email);
    if (user && user.name) setName(user.name);
  }, [user]);

  // If prefill changes (e.g. chat escalation passes a new last question), reflect it.
  useEffect(() => {
    if (prefill.message !== undefined) setMessage(prefill.message);
    if (prefill.subject !== undefined) setSubject(prefill.subject);
  }, [prefill.message, prefill.subject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus('error');
      setErrorMsg(ja ? 'お名前、メールアドレス、メッセージは必須です。' : 'Name, email, and message are required.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const body = {
        name: name.trim() || null,
        email: email.trim(),
        subject: subject.trim() || (prefill.escalated_to_support ? 'Chat escalation: customer needs help' : 'Support Request'),
        message: message.trim(),
        source_page: prefill.source_page || sourcePage || (typeof window !== 'undefined' ? window.location.pathname : ''),
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        last_chat_question: prefill.last_chat_question || null,
        chat_context_summary: prefill.chat_context_summary || null,
        escalated_to_support: !!prefill.escalated_to_support,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        // Surface which JTR brand-domain the customer was on (.jp vs .com) so
        // support staff can sort/route by language at a glance.
        origin_domain: typeof window !== 'undefined' ? window.location.hostname : null,
      };
      const res = await fetch(`${API}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setStatus('error');
        setErrorMsg(ja ? 'メッセージを送信できませんでした。もう一度お試しください。' : 'We could not send your message. Please try again.');
        return;
      }
      setStatus('success');
      if (!user) setName('');
      setSubject('');
      setMessage('');
      if (onSubmitted) onSubmitted(data);
    } catch {
      setStatus('error');
      setErrorMsg(ja ? 'メッセージを送信できませんでした。もう一度お試しください。' : 'We could not send your message. Please try again.');
    }
  };

  const isPanel = layout === 'panel';
  const labelCls = 'text-sm font-medium text-gray-700';
  const inputCls = 'mt-1.5 w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 text-sm';

  if (status === 'success') {
    return (
      <div className={`text-center ${isPanel ? 'py-6 px-4' : 'py-10 px-6'}`} data-testid="support-form-success">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Check className="w-6 h-6 text-emerald-600" strokeWidth={2} />
        </div>
        <h3 className="mt-3 text-base font-bold text-gray-900">
          {ja ? 'ありがとうございます。' : 'Thank you.'}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {ja ? 'サポートへメッセージを送信しました。' : 'Your message was sent to support.'}
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-4 text-xs font-bold text-purple-700 hover:underline"
          data-testid="support-form-send-another"
        >
          {ja ? '別のメッセージを送信する' : 'Send another message'}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className={isPanel ? 'space-y-3' : 'space-y-4'} data-testid="support-form">
      {showHeader && layout === 'page' && (
        <h2 className="text-base font-bold text-gray-900">
          {ja ? 'メッセージを送る' : 'Send Us a Message'}
        </h2>
      )}

      {prefill.last_chat_question && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="support-form-chat-context">
          <p className="font-semibold">{ja ? 'チャットから引き継ぎ' : 'Imported from chat'}</p>
          <p className="mt-1 whitespace-pre-wrap">{prefill.last_chat_question}</p>
        </div>
      )}

      <div>
        <label htmlFor="support-name" className={labelCls}>
          {ja ? 'お名前' : 'Your Name'} <span className="text-red-500">*</span>
        </label>
        <input
          id="support-name" name="name" autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputCls}
          placeholder={ja ? 'お名前' : 'Your name'}
          data-testid="support-form-name"
        />
      </div>
      <div>
        <label htmlFor="support-email" className={labelCls}>
          {ja ? 'メールアドレス' : 'Email Address'} <span className="text-red-500">*</span>
        </label>
        <input
          id="support-email" name="email" autoComplete="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          placeholder="you@example.com"
          data-testid="support-form-email"
        />
      </div>
      <div>
        <label htmlFor="support-message" className={labelCls}>
          {ja ? 'メッセージ' : 'Message'} <span className="text-red-500">*</span>
        </label>
        <textarea
          id="support-message" name="message"
          required
          rows={isPanel ? 3 : 5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputCls} min-h-[80px]`}
          placeholder={ja ? 'お問い合わせ内容…' : 'Tell us what we can help with...'}
          data-testid="support-form-message"
        />
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900" data-testid="support-form-privacy-warning">
        <ShieldAlert className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <p>
          {ja
            ? 'パスワード、決済情報、その他の機密情報は送信しないでください。'
            : 'Please do not send passwords, payment details, or sensitive credentials through this form.'}
        </p>
      </div>

      {status === 'error' && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="support-form-error">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <span>{errorMsg || (ja ? 'メッセージを送信できませんでした。' : 'We could not send your message.')}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-3 rounded-lg font-bold text-white text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: '#9200C7' }}
        data-testid="support-form-submit"
      >
        {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitLabel || (ja ? 'メッセージを送信' : 'Send Message')}
      </button>
    </form>
  );
}
